import { useEffect } from "react";
import type { VideoSourceState } from "@core/features/video";
import { usePlatformStore } from "@platform/store";
import type { ChimeObserverState } from "./chimeObserverState";

/**
 * Syncs `activeSpeakers` into `videoSourceState.activeSpeakersWithCameraSource`
 * and triggers a preference update. Active-speaker churn is the noisy
 * estimator-adjacent caller that the hysteresis cooldown is designed to
 * dampen, so this call deliberately does NOT bypass hysteresis — rapid-
 * fire speaker transitions collapse to one chooseRemoteVideoSources call
 * per cooldown window, with the latest committed state.
 */
export function useActiveSpeakerDownlinkSync(state: ChimeObserverState) {
  const activeSpeakers = usePlatformStore((s) => s.activeSpeakers);

  useEffect(() => {
    const s = usePlatformStore.getState();
    const { attendeeTileState, videoSourceState } = s;
    const { cameraSources } = videoSourceState;
    const activeSpeakersWithCameraSource: string[] = [];

    for (const attendeeId of activeSpeakers) {
      const ts = attendeeTileState[attendeeId];
      if (
        (ts === 'video' || ts === 'frozen') &&
        cameraSources.includes(attendeeId)
      ) {
        activeSpeakersWithCameraSource.push(attendeeId);
      }
    }

    const nextVideoSourceState: VideoSourceState = {
      ...videoSourceState,
      activeSpeakersWithCameraSource,
    };

    s.setVideoSourceState(nextVideoSourceState);
    state.applyPreferenceUpdate();
  }, [activeSpeakers, state]);
}
